import { SOCIAL_ASSET_CHANNELS, type SocialAssetWithProject } from '../../../shared/types';
import { BrandBadge, formatWeekdayDay } from '../../components/primitives';
import { assetChannels, channelLabel, CHANNEL_LABELS, DOW, monthLabel, shiftMonth, TYPE_LABELS } from '../../lib/socialCalendar';
import { FilterChip } from './chrome';
import { type SocialCalendarProps } from './types';

function slotLabel(asset: SocialAssetWithProject): string {
  const parts = [channelLabel(assetChannels(asset.channels)), asset.label];
  if (asset.project_name !== null) parts.push(asset.project_name);
  return parts.join(' · ') + (asset.status === 'needs_prep' ? ' (prep)' : '');
}

export function SocialCalendarBody({
  month,
  weeks,
  channel,
  onMonth,
  onChannel,
  ready,
  today,
  selectedSlotId,
  onSelectSlot,
  onFillWeek,
  actions,
  popover,
}: SocialCalendarProps) {
  return (
    <div data-social-view="calendar">
      <div className="social-calhead">
        <button type="button" className="social-calnav" onClick={() => onMonth(shiftMonth(month, -1))}>
          ←
        </button>
        <span className="mth">{monthLabel(month)}</span>
        <button type="button" className="social-calnav" onClick={() => onMonth(shiftMonth(month, 1))}>
          →
        </button>
        <span className="social-chn">
          channels:
          <FilterChip label="all" on={channel === 'all'} onClick={() => onChannel('all')} />
          {SOCIAL_ASSET_CHANNELS.map((c) => (
            <FilterChip
              key={c}
              label={CHANNEL_LABELS[c]}
              on={channel === c}
              onClick={() => onChannel(c)}
            />
          ))}
        </span>
      </div>

      <div className="social-cal">
        {DOW.map((d) => (
          <div className="social-dow" key={d}>
            {d}
          </div>
        ))}
        {weeks.map((week) =>
          week.gap ? (
            <div className="social-gap" key={week.start}>
              <span>
                Gap · {formatWeekdayDay(week.start)} to {formatWeekdayDay(week.end)} · nothing planned
              </span>
              {ready.length > 0 && week.end >= today && (
                <button type="button" className="act" onClick={() => onFillWeek(week)}>
                  fill from {ready.length} ready
                </button>
              )}
            </div>
          ) : (
            week.days.map((day) => (
              <div
                className={`social-day${day.inMonth ? '' : ' out'}${day.isToday ? ' today' : ''}`}
                key={day.iso}
              >
                <span className="dn">{day.day}</span>
                {day.slots.map((asset) => (
                  <button
                    type="button"
                    key={asset.id}
                    title={slotLabel(asset)}
                    className={[
                      'social-slot',
                      asset.brand === 'ss' ? 'ss' : '',
                      asset.status === 'used' ? 'done' : '',
                      asset.status === 'needs_prep' ? 'prep' : '',
                      selectedSlotId === asset.id ? 'sel' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => onSelectSlot(selectedSlotId === asset.id ? null : asset.id)}
                  >
                    {slotLabel(asset)}
                  </button>
                ))}
              </div>
            ))
          ),
        )}
      </div>

      {popover}

      <div className="social-tray">
        <div className="tl">ready to slot · click a card to place it</div>
        <div className="items">
          {ready.map((asset) => (
            <button
              type="button"
              className="social-card"
              key={asset.id}
              onClick={() => actions.onStartSchedule(asset)}
            >
              <span className="pill social-type">{TYPE_LABELS[asset.type]}</span>
              <span className="lbl">{asset.label}</span>
              <BrandBadge brand={asset.brand} />
            </button>
          ))}
          {ready.length === 0 && <span className="empty-note">Nothing ready to slot.</span>}
        </div>
      </div>
    </div>
  );
}
